import {
  SupportSessionDrawer,
  SupportSessionsTable,
  useSession,
  useSupportSessionMutations,
  useSupportSessions,
  type RequestSupportSessionInput,
  type SupportSession,
} from "@how/app-kit";
import {
  Button,
  Card,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery } from "@tanstack/react-query";
import { IconPlus } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { ConfirmActionModal, PageHeader } from "@how/ui";
import { supabase } from "../../lib/supabase";
import { usePlatformRole } from "../auth/platform-role";
import { sessionPermissionOptions } from "./support-permissions";

interface RequestValues {
  tenantId: string;
  purpose: string;
  accessLevel: "read_only" | "read_write";
  durationMinutes: number;
  permissions: string[];
}

/** Every tenant the platform may pick for a session (deleted ones cannot be requested). */
function useTenantOptions() {
  return useQuery({
    queryKey: ["tenant-options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenants")
        .select("id, name, slug, status")
        .neq("status", "deleted")
        .order("name");
      if (error) throw error;
      return data;
    },
  });
}

/** Request, watch, break-glass approve, and end support sessions (docs/security/support-access.md). */
export function SupportSessionsPage() {
  const session = useSession();
  const role = usePlatformRole();
  const sessions = useSupportSessions();
  const tenants = useTenantOptions();
  const mutations = useSupportSessionMutations();
  const [selected, setSelected] = useState<SupportSession | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [breakGlassFor, setBreakGlassFor] = useState<SupportSession | null>(null);
  const [justification, setJustification] = useState("");
  const [ending, setEnding] = useState<SupportSession | null>(null);
  const me = session.status === "signed_in" ? session.session.user.id : null;
  const isSuperAdmin = role.data === "super_admin";
  const tenantNames = useMemo(
    () => new Map((tenants.data ?? []).map((t) => [t.id, t.name] as const)),
    [tenants.data],
  );
  const current =
    selected === null ? null : (sessions.data?.find((s) => s.id === selected.id) ?? selected);

  const form = useForm<RequestValues>({
    initialValues: {
      tenantId: "",
      purpose: "",
      accessLevel: "read_only",
      durationMinutes: 120,
      permissions: [],
    },
    validate: {
      tenantId: (v) => (v.length === 0 ? "Choose a church" : null),
      purpose: (v) =>
        v.trim().length < 10 ? "Describe the purpose in at least 10 characters" : null,
      durationMinutes: (v) => (v < 15 || v > 480 ? "Between 15 minutes and 8 hours" : null),
    },
  });
  const permissionOptions = useMemo(
    () => sessionPermissionOptions(form.values.accessLevel),
    [form.values.accessLevel],
  );

  const submit = (values: RequestValues): void => {
    const input: RequestSupportSessionInput = {
      tenantId: values.tenantId,
      purpose: values.purpose.trim(),
      accessLevel: values.accessLevel,
      durationMinutes: values.durationMinutes,
      ...(values.permissions.length === 0 ? {} : { permissions: values.permissions }),
    };
    mutations.request.mutate(input, {
      onSuccess: () => {
        setRequesting(false);
        form.reset();
      },
    });
  };

  const canEnd = (s: SupportSession) =>
    (s.status === "requested" || s.status === "active") &&
    (s.platformUserId === me || isSuperAdmin);
  const canBreakGlass = (s: SupportSession) =>
    s.status === "requested" &&
    s.accessLevel === "read_write" &&
    isSuperAdmin &&
    s.platformUserId !== me;

  return (
    <>
      <PageHeader
        title="Support sessions"
        description="Time-bound, audited access to a church. Read-only starts at once; read and write waits for the church or a break-glass approval."
        actions={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setRequesting(true);
            }}
          >
            Request session
          </Button>
        }
      />
      <Card>
        <SupportSessionsTable
          sessions={sessions.data}
          isPending={sessions.isPending}
          error={sessions.error}
          onRetry={() => {
            void sessions.refetch();
          }}
          onSelect={setSelected}
          tenantNames={tenantNames}
          emptyDescription="Sessions you request, and every session when you are a super admin, appear here."
        />
      </Card>

      <SupportSessionDrawer
        session={current}
        onClose={() => {
          setSelected(null);
        }}
        {...(current === null
          ? {}
          : { tenantName: tenantNames.get(current.tenantId) ?? current.tenantId })}
        actions={
          current === null ? null : (
            <Group gap="sm">
              {canBreakGlass(current) ? (
                <Button
                  variant="light"
                  color="red"
                  onClick={() => {
                    setJustification("");
                    setBreakGlassFor(current);
                  }}
                >
                  Break-glass approve
                </Button>
              ) : null}
              {canEnd(current) ? (
                <Button
                  variant="light"
                  onClick={() => {
                    setEnding(current);
                  }}
                >
                  {current.status === "requested" ? "Withdraw request" : "End session"}
                </Button>
              ) : null}
            </Group>
          )
        }
      />

      <Modal
        opened={requesting}
        onClose={() => {
          setRequesting(false);
        }}
        title="Request a support session"
        size="lg"
      >
        <form onSubmit={form.onSubmit(submit)}>
          <Stack gap="sm">
            <Select
              label="Church"
              placeholder="Choose a church"
              searchable
              data={(tenants.data ?? []).map((t) => ({
                value: t.id,
                label: `${t.name} (${t.slug})`,
              }))}
              {...form.getInputProps("tenantId")}
            />
            <TextInput
              label="Purpose"
              description="Shown to the church and written to the audit log."
              placeholder="Investigate the failing invitation email reported in ticket 1234"
              {...form.getInputProps("purpose")}
            />
            <SegmentedControl
              fullWidth
              data={[
                { value: "read_only", label: "Read only (starts now)" },
                { value: "read_write", label: "Read and write (needs approval)" },
              ]}
              {...form.getInputProps("accessLevel")}
            />
            <NumberInput
              label="Duration (minutes)"
              min={15}
              max={480}
              step={15}
              {...form.getInputProps("durationMinutes")}
            />
            <MultiSelect
              label="Limit the scope"
              description="Leave empty for every non-sensitive permission. users.manage and domains.manage_signin can never be granted."
              searchable
              clearable
              data={permissionOptions}
              {...form.getInputProps("permissions")}
            />
            <Group justify="flex-end" mt="sm">
              <Button
                variant="subtle"
                color="gray"
                onClick={() => {
                  setRequesting(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={mutations.request.isPending}>
                Request
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={breakGlassFor !== null}
        onClose={() => {
          setBreakGlassFor(null);
        }}
        title="Break-glass approval"
      >
        <Stack gap="sm">
          <Text size="sm">
            You are approving read and write access on behalf of the church. This is recorded as
            break-glass with your justification, and the church's administrators can see and revoke
            it.
          </Text>
          <Textarea
            label="Justification"
            description="At least 20 characters."
            minRows={3}
            value={justification}
            onChange={(event) => {
              setJustification(event.currentTarget.value);
            }}
          />
          <Group justify="flex-end">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => {
                setBreakGlassFor(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={mutations.breakGlass.isPending}
              disabled={justification.trim().length < 20}
              onClick={() => {
                if (breakGlassFor === null) return;
                mutations.breakGlass.mutate(
                  { sessionId: breakGlassFor.id, justification: justification.trim() },
                  {
                    onSuccess: () => {
                      setBreakGlassFor(null);
                    },
                  },
                );
              }}
            >
              Approve with break-glass
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ConfirmActionModal
        opened={ending !== null}
        onClose={() => {
          setEnding(null);
        }}
        title={ending?.status === "requested" ? "Withdraw this request?" : "End this session?"}
        message="Access stops immediately and the change is written to the audit log."
        confirmLabel={ending?.status === "requested" ? "Withdraw" : "End session"}
        danger
        loading={mutations.end.isPending}
        onConfirm={() => {
          if (ending === null) return;
          mutations.end.mutate(ending.id, {
            onSuccess: () => {
              setEnding(null);
              setSelected(null);
            },
          });
        }}
      />
    </>
  );
}
